import {
  useDeleteAccessKey,
  useGetAccessKeys,
} from '@/hooks/queries/access-keys/index';
import { Icon } from '@iconify/react';
import { Button, Spinner } from '@nextui-org/react';
import { useMemo, useState } from 'react';
import DataView from '../../components/data-view/data-view';
import { TableConfig } from '../../components/table/types';
import ConfigureApiKeyModal from './configure-api-key-modal';

interface ApiProvider {
  id: string;
  name: string;
  status: string;
  created: string;
  icon: string;
}

export default function ApiProvidersPage() {
  const [selectedProvider, setSelectedProvider] = useState<ApiProvider | null>(
    null
  );
  const [isAddKeyModalOpen, setIsAddKeyModalOpen] = useState(false);
  const { data: accessKeys, isLoading, error } = useGetAccessKeys();

  const { mutate: deleteAccessKey } = useDeleteAccessKey({
    onSuccess: () => {
      setSelectedProvider(null);
    },
  });

  const handleDelete = (provider: ApiProvider) => {
    if (window.confirm(`Are you sure you want to delete ${provider.name}?`)) {
      deleteAccessKey(Number(provider.id));
    }
  };

  const providers: ApiProvider[] = useMemo(() => {
    if (!accessKeys || !accessKeys.results) return [];

    return accessKeys.results.map((key: any) => ({
      id: key.id,
      name: key.name,
      status: key.plain_key ? 'Configured' : 'Not configured',
      created: key.created_at ? new Date(key.created_at).toLocaleString() : '-',
      icon: key.name.toLowerCase(),
      plain_key: key.plain_key,
    }));
  }, [accessKeys]);

  const tableConfig = useMemo<TableConfig>(
    () => ({
      columns: [
        {
          uid: 'name',
          name: 'Name',
        },
        {
          uid: 'status',
          name: 'Status',
          renderCell: (provider: ApiProvider) => (
            <div className="flex items-center">
              <span
                className={`${provider.status === 'Configured' ? 'text-success' : 'text-danger'} mr-2`}
              >
                ●
              </span>
              {provider.status}
            </div>
          ),
        },
        {
          uid: 'created',
          name: 'Created',
        },
        {
          uid: 'actions',
          name: 'Actions',
          align: 'end',
          width: '120',
          renderCell: (provider: ApiProvider) => (
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="cursor-pointer"
                onClick={() => setSelectedProvider(provider)}
                aria-label="Edit"
              >
                <Icon icon="akar-icons:edit" width="20" />
              </button>
              <button
                type="button"
                className="cursor-pointer text-default-400"
                onClick={() => handleDelete(provider)}
                aria-label="Delete"
              >
                <Icon icon="proicons:delete" width="20" />
              </button>
            </div>
          ),
        },
      ],
      initialVisibleColumns: ['name', 'status', 'created', 'actions'],
      bottomContentPlacement: 'outside',
      tdClassName: 'py-2.5',
    }),
    []
  );

  const actions = (
    <Button
      color="primary"
      size="sm"
      onClick={() => setIsAddKeyModalOpen(true)}
      startContent={<Icon icon="mdi:plus" />}
    >
      Add Key
    </Button>
  );

  if (isLoading) {
    return (
      <div className="flex h-full min-h-0 flex-1 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center text-danger">
        Error loading AI providers. Please try again later.
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <DataView
        disableTileView
        title="AI Providers"
        actions={actions}
        data={providers}
        tableConfig={tableConfig}
        viewType="list"
        onViewChange={() => {}}
        renderCard={() => null}
      />

      {!!selectedProvider && (
        <ConfigureApiKeyModal
          isOpen={!!selectedProvider}
          onClose={() => setSelectedProvider(null)}
          provider={selectedProvider}
        />
      )}

      {isAddKeyModalOpen && (
        <ConfigureApiKeyModal
          isOpen={isAddKeyModalOpen}
          onClose={() => setIsAddKeyModalOpen(false)}
          provider={null}
        />
      )}
    </div>
  );
}
